<script>
  import { Button, Popover, Tooltip } from "sveltestrap";
  import { tick } from "svelte";

  export let searchUrlQueryParam;
  export let tooltip;

  const tooltipElementId = `${searchUrlQueryParam}-tooltip-id`;
  const tooltipContents = (tooltip && tooltip.startsWith("#") && document.getElementById(tooltip.substring(1))) ? document.getElementById(tooltip.substring(1)).innerHTML : tooltip;

  let timer;
  let popoverElement;
  let isOpen = false;

  function hidePopover() {
    timer = setTimeout(() => {
      isOpen = false;
    }, 200); // 200ms grace period to move mouse to popover
  }

  function showPopover() {
    clearTimeout(timer);
    isOpen = true;
  }

  function hideTooltip() {
    timer = setTimeout(() => {
      isOpen = false;
    }, 50);
  }

  function showTooltip() {
    clearTimeout(timer);
    isOpen = true;
  }


  // keydown logic for div behind the button, because the button didn't want to cooperate.
  async function handleTriggerKeydown(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault(); // STOP SCROLLING WHEN I PRESS SPACE
      e.stopPropagation(); // this makes enter start working
      isOpen = !isOpen;
      // just 508 things, when the popover is triggered by a keydown event highlight the link immediately
      if (isOpen) {
        await tick();
        popoverElement?.querySelector("a")?.focus();
      }
    }
  }

  async function handlePopoverKeydown(e) {
    if (e.key === "Enter" && e.target.tagName === "A") {
      e.stopPropagation();
      // delay, otherwise the popover closes before following the link
      setTimeout(() => {
        isOpen = false;
        let returnElement = document.getElementById(tooltipElementId);
        returnElement.focus();
      }, 100);
      return;
    }

    // escape to close popover window
    if (e.key === "Escape") {
      isOpen = false;
      let returnElement = document.getElementById(tooltipElementId);
      returnElement.focus();
      return;
    }

    // trap tab from leaving the popover, add logic to visit interactive's in the popover instead
    if (e.key === "Tab") {
      const focusables = popoverElement.querySelectorAll("a, button, [tabindex=\"0\"]");
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // stop space from scrolling when pressed in the popover
    if (e.key === " ") {
      e.preventDefault();
    }
  }
</script>

<div
  on:mouseenter={tooltipContents.includes("<a href=") ? showPopover : showTooltip}
  on:mouseleave={tooltipContents.includes("<a href=") ? hidePopover : hideTooltip}
  on:keydown={handleTriggerKeydown}
  class="custom-css-class"
  style="display: inline-block; line-height: 0;"
>
  <Button
    id={tooltipElementId}
    class="btn btn-link mb-1 ps-1"
    aria-label="{searchUrlQueryParam} tooltip button. Press enter or space to open for more information."
    aria-describedby="{searchUrlQueryParam}-tooltip-aria"
    aria-expanded={isOpen}
    aria-haspopup="true"
    style="pointer-events: none;"
  >
    <i class="bi bi-question-circle-fill" aria-hidden="true"></i>
  </Button>
  {#if tooltipContents.includes("<a href=")}
    <Popover bind:isOpen id="{searchUrlQueryParam}-tooltip-aria" target={tooltipElementId} placement="right"
             class="custom-dark-theme">
      <div
        bind:this={popoverElement}
        id="{searchUrlQueryParam}-popover"
        on:mouseenter={showPopover}
        on:mouseleave={hidePopover}
        on:keydown={handlePopoverKeydown}
      >
        <p>
          {@html tooltipContents}
        </p>
      </div>
    </Popover>
  {:else}
    <Tooltip
      id="{searchUrlQueryParam}-tooltip-aria"
      target={tooltipElementId}
      placement="right"
      role="tooltip"
      isOpen={isOpen}
    >
      <p>
        {@html tooltipContents}
      </p>
    </Tooltip>
  {/if}
</div>

<style>
    /* Use :global to ensure we override the sveltestrap/bootstrap defaults

       Also seems to be overriding all styles, even outside this component
    */
    .custom-css-class :global(.btn-link) {
        background-color: transparent !important;
        box-shadow: none !important;
        color: #1E2125 !important;

        transition: outline 0.2s ease-out,
        outline-offset 0.2s ease-out,
        background-color 0.2s ease-out !important;

        outline: 2px solid transparent !important;
        outline-offset: 0;
    }

    /* Accessible Focus State */
    .custom-css-class :global(.btn-link:focus) {
        box-shadow: none !important;
        background-color: transparent !important;
        outline: 2px solid #0d6efd !important;
        outline-offset: 2px;
    }

    /* Target links inside any popover that has been styled dark */
    :global(.popover.custom-dark-theme a) {
        color: #0dcaf0 !important; /* A bright cyan/light blue that pops on dark backgrounds */
        text-decoration: none;
    }

    /* Optional: Change the color when the user hovers over the link */
    :global(.popover.custom-dark-theme a:hover) {
        color: #0dcaf0 !important;
        text-decoration: underline;
    }

    :global(.popover.custom-dark-theme) {
        --bs-popover-bg: #212529 !important;
        --bs-popover-color: #fff !important;
        --bs-popover-body-color: #fff !important;
        /* 1. Set the border to match the background exactly */
        /*--bs-popover-border-color: #212529 !important;*/

        /* 2. Optional: If you want a subtle dark gray border to separate it from other dark areas */
        --bs-popover-border-color: #444 !important;

        /* 3. Remove any potential box-shadow that might look like a white glow */
        box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.5) !important;
    }

    /* 4. Update the arrow border to match the new color */
    :global(.popover.custom-dark-theme .popover-arrow::before) {
        border-right-color: #212529 !important; /* Matches 'placement="right"' */
    }
</style>